import React, { useState, useEffect } from "react";
import { Sidebar } from "../components/Sidebar";
import { auth, db, storage } from "../pages/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { toast } from "react-toastify";
import { FaCamera } from 'react-icons/fa'; // Import camera icon
import Avatar from 'react-avatar'; // Import Avatar component
import profile from "../assets/Profile.jpg";

export default function Ap_Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editable, setEditable] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    photo: "",
  });
  const [image, setImage] = useState(null); // Track the image file
  const [imagePreview, setImagePreview] = useState(null); // Store the image preview before saving
  const [savingForm, setSavingForm] = useState(false); // Track form saving state
  const backgroundImageStyle = {
    backgroundImage: `url(${profile})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed', // Keeps the image fixed as you scroll
    minHeight: '100vh', // Full page height
    backgroundColor: 'rgba(0, 0, 0, 30)'
  };
  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)', // Dim the background (adjust the opacity here)
    zIndex: 1, // Make sure the overlay is above the background
  };
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        await fetchProfileData(authUser.uid); // Fetch profile data on user login
      } else {
        setUser(null);
        setProfileData({ 
          firstName: "",
          lastName: "",
          email: "",
          mobile: "",
          photo: "",
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const fetchProfileData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, "user", uid));
      if (userDoc.exists()) {
        setProfileData(userDoc.data());
      }
    } catch (error) {
      toast.error("Error fetching user data");
      console.error("Error fetching user data: ", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result); // Set the preview of the selected image
      };
      reader.readAsDataURL(file);
      setImage(file); // Save the file in state
    }
  };

  const handleImageUpdate = async () => {
    if (!image || !user) return;

    setSavingForm(true);
    try {
      const imageRef = ref(storage, `profiles/${user.uid}/${image.name}`);
      await uploadBytes(imageRef, image);
      const imageUrl = await getDownloadURL(imageRef);

      await setDoc(
        doc(db, "user", user.uid),
        {
          ...profileData,
          photo: imageUrl,
        },
        { merge: true }
      );

      setProfileData((prevData) => ({
        ...prevData,
        photo: imageUrl,
      }));

      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile picture");
      console.error("Error updating profile picture: ", error);
    } finally {
      setSavingForm(false);
    }
  };

  const handleSave = async () => {
    setSavingForm(true);
    try {
      if (editable) {
        if (image) {
          await handleImageUpdate();
        } else {
          await setDoc(doc(db, "user", user.uid), profileData, { merge: true });
          toast.success("Profile updated successfully");
        }
        setEditable(false); // Return to view mode after saving
        await fetchProfileData(user.uid); // Fetch updated profile data after save
      }
    } catch (error) {
      toast.error("Failed to update profile");
      console.error("Error updating user data: ", error);
    } finally {
      setSavingForm(false);
    }
  };

  const handleEdit = () => {
    setEditable(true); // Set editable to true to show Save button
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <svg
          className="w-12 h-12 text-gray-500 animate-spin"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            className="opacity-25"
          />
          <path
            d="M4 12a8 8 0 0116 0 8 8 0 01-16 0"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row overflow-auto min-h-screen p-4" style={backgroundImageStyle}>
      <Sidebar user={user} />
      <div className="flex-1 mt-6 md:mt-10 mb-6 md:mb-12 p-4 md:p-12 mx-auto w-full max-w-md md:max-w-2xl lg:max-w-2xl bg-[#4d5e6e] bg-opacity-90 rounded-lg shadow-lg">
        <h1 className="text-2xl text-white text-center font-bold mb-6">Profile</h1>

        <div className="flex justify-center mb-6">
          <div className="w-28 h-28 border border-gray-300 bg-gray-200 flex items-center justify-center rounded-full overflow-hidden relative">
            {editable ? (
              <img
                src={imagePreview || profileData.photo || "https://via.placeholder.com/150"}
                alt="User"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <Avatar
                name={`${profileData.firstName} ${profileData.lastName}`}
                size="150"
                round
                src={profileData.photo || null}
                className="w-full h-full object-cover"
              />
            )}
            {editable && (
              <label htmlFor="file-upload" className="absolute bottom-0 right-2 bg-white p-1 rounded-full cursor-pointer">
                <FaCamera className="text-xl text-gray-600" />
              </label>
            )}
          </div>
          <input id="file-upload" type="file" onChange={handleImageChange} className="hidden" />
        </div>

        <form className="space-y-4">
          <div className="flex flex-col md:flex-row md:space-x-4">
            <div className="flex-1">
              <label className="block text-white mb-1">First Name</label>
              <input
                type="text"
                name="firstName"
                value={profileData.firstName || ""}
                className={`w-full px-4 py-2 border rounded-md bg-gray-100 transition-all duration-300 ${editable ? "border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" : "border-gray-300"}`}
                readOnly={!editable}
                onChange={handleChange}
              />
            </div>
            <div className="flex-1">
              <label className="block text-white mb-1">Last Name</label>
              <input
                type="text"
                name="lastName"
                value={profileData.lastName || ""}
                className={`w-full px-4 py-2 border rounded-md bg-gray-100 transition-all duration-300 ${editable ? "border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" : "border-gray-300"}`}
                readOnly={!editable}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <label className="block text-white mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={profileData.email || ""}
              className="w-full px-4 py-2 border rounded-md bg-gray-100 border-gray-300 text-gray-800"
              readOnly
            />
            <span className="text-white text-sm">Email cannot be edited.</span>
          </div>

          <div>
            <label className="block text-white mb-1">Phone Number</label>
            <input
              type="text"
              name="mobile"
              value={profileData.mobile || ""}
              className={`w-full px-4 py-2 border rounded-md bg-gray-100 transition-all duration-300 ${editable ? "border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" : "border-gray-300"}`}
              readOnly={!editable}
              onChange={handleChange}
            />
          </div>

          <div className="text-center mt-4">
            {!editable ? (
              <button
                type="button"
                className="bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition duration-300"
                onClick={handleEdit}
              >
                Edit
              </button>
            ) : (
              <button
                type="button"
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-400 transition duration-300"
                onClick={handleSave}
                disabled={savingForm}
              >
                {savingForm ? "Saving..." : "Save"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
